import { EmailUtils } from "@stellar/utils";
import { getSelectedOptionValues, validateInviteMembers } from "@utils/members-invite-utils";
import { useState } from "react";

/** Minimal shape of an entity member needed to detect duplicates. */

/**
 * Encapsulates the shared autocomplete workflow used when inviting members to a
 * project, group, snapshot or team (and when creating a group/team with
 * members). It manages the selected options and the validation messages while
 * leaving the entity specific submit logic to the consumer.
 *
 * Validation is split in two parts:
 * - `inputValidation` (state) reflects the text currently typed in the input.
 * - `selectionValidation` (derived) reflects the selected members (max limit,
 *   duplicates and new emails). Deriving it on every render keeps the message
 *   correct even when the autocomplete fires an input-change event right after
 *   a paste, which would otherwise clear the freshly computed message.
 *
 * The input message takes priority while the user is typing; otherwise the
 * selection-level message is shown.
 */
export function useMembersAutocomplete({
  entityName,
  existingMembers,
  options,
  companyMemberIds,
  maxInvitedMembers,
  invalidInputMessage = "Invalid Email or Doesn't match any team or member.",
  alreadyMemberMessage = `A member with the same email already exists in the ${entityName}`,
  canInviteNewEmails = true
}) {
  const [selectedMembers, setSelectedMembers] = useState([]);
  const [inputValidation, setInputValidation] = useState({
    message: undefined,
    isValid: true
  });
  const selectedValues = getSelectedOptionValues(selectedMembers);
  const uniqueSelectedValues = Array.from(new Set(selectedValues));

  // Selection-level validation (max limit, duplicates, new emails to
  // workspace). When `maxInvitedMembers` is not provided the selection is left
  // uncapped, which is the case for every flow except the team flows.
  const selectionValidation = validateInviteMembers({
    entityName,
    selectedOptions: selectedMembers,
    companyMemberIds,
    maxInvitedMembers,
    canInviteNewEmails
  });
  const helperText = inputValidation.message ?? selectionValidation.message;
  const isMemberInputValid = inputValidation.isValid && selectionValidation.isValid;

  /** Returns true if the value already belongs to the entity members. */
  function isAlreadyMember(value) {
    // Check if the value matches any existing member's identity or email
    // Both identity and email are checked to ensure that we catch duplicates regardless of which field the user inputs.
    return existingMembers.some(member => member.identity === value || member.email.toLowerCase() === value.toLocaleLowerCase().trim());
  }

  /**
   * Returns the option the typed text identifies, matching both the identity an option carries and
   * the email shown as its subtitle, so a member can be typed as either of them. The title is left
   * out on purpose, since a display name is neither unique nor something the caller can resolve
   * back to a single member.
   */
  function findOptionByIdentifier(value_0) {
    const lowerCasedValue = value_0.toLowerCase().trim();
    return options.find(option => option.value.toLowerCase() === lowerCasedValue || !!option.subtitle && option.subtitle.toLowerCase() === lowerCasedValue);
  }

  /** Returns true if the typed value can be turned into a selection. */
  function isNewEmailValid(value_1) {
    // Only the given options are selectable, so text that does not identify one of them (or that
    // identifies an option which cannot be picked) is refused instead of becoming a new email.
    if (!canInviteNewEmails) {
      const option_0 = findOptionByIdentifier(value_1);
      return !!option_0 && !option_0.isDisabled && !isAlreadyMember(value_1);
    }
    return EmailUtils.isValidEmail(value_1) && !isAlreadyMember(value_1);
  }
  function handleMemberSelect(newOptions) {
    setSelectedMembers(newOptions);
  }

  /** Validates the text currently typed in the autocomplete input. */
  function validateInput(value_2) {
    if (!value_2) {
      return {
        message: undefined,
        isValid: true
      };
    }

    // The typed email already belongs to the entity.
    if (isAlreadyMember(value_2)) {
      return {
        message: {
          type: "info",
          text: alreadyMemberMessage
        },
        isValid: false
      };
    }

    // The typed text stands for an option that cannot be picked, so the reason it is disabled for
    // is surfaced instead of letting the selection fail silently.
    const matchingOption = findOptionByIdentifier(value_2);
    if (matchingOption?.isDisabled && matchingOption.disabledMessage) {
      return {
        message: {
          type: "info",
          text: matchingOption.disabledMessage
        },
        isValid: false
      };
    }
    const lowerCasedValue_0 = value_2.toLowerCase();
    const isValueInOptions = options.some(option_1 => option_1.title.toLowerCase().includes(lowerCasedValue_0) || option_1.subtitle.toLowerCase().includes(lowerCasedValue_0));

    // New text that matches no existing option, and that cannot fall back to being a new email
    // when only the given options are selectable.
    if (!isValueInOptions && (!canInviteNewEmails || !EmailUtils.isValidEmail(value_2))) {
      return {
        message: {
          type: "error",
          text: invalidInputMessage
        },
        isValid: false
      };
    }
    return {
      message: undefined,
      isValid: true
    };
  }
  function onInputChange(value_3) {
    setInputValidation(validateInput(value_3));
  }
  function reset() {
    setSelectedMembers([]);
    setInputValidation({
      message: undefined,
      isValid: true
    });
  }
  return {
    selectedMembers,
    setSelectedMembers,
    selectedValues,
    uniqueSelectedValues,
    helperText,
    isMemberInputValid,
    handleMemberSelect,
    onInputChange,
    isNewEmailValid,
    isAlreadyMember,
    reset
  };
}